import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Save, Trash2, AlertTriangle, Settings, Users, Database, Globe, Edit, BarChart3, GitPullRequest, GitFork, Copy, Check } from 'lucide-react';
import { 
  getPromptSettings, 
  updatePromptSettings, 
  deletePrompt, 
  searchUsersForMaintainers
} from '../services/api';

const PromptSettings = ({ user }) => {
  const { promptId } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [testSets, setTestSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [copiedPromptId, setCopiedPromptId] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    owner_id: '',
    maintainers: [],
    enable_logging: true,
    auto_eval_enabled: false,
    auto_eval_test_set_id: '',
    auto_eval_model: 'llama-3.1-8b-instant',
    auto_eval_threshold: 0.7
  });

  useEffect(() => {
    loadData();
  }, [promptId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const promptData = await getPromptSettings(promptId);
      
      setPrompt(promptData);
      
      setFormData({
        name: promptData.name || '',
        description: promptData.description || '',
        owner_id: promptData.owner_id || null,
        maintainers: promptData.maintainers || [],
        enable_logging: promptData.enable_logging !== false,
        auto_eval_enabled: promptData.auto_eval_settings?.enabled || false,
        auto_eval_test_set_id: promptData.auto_eval_settings?.test_set_id || null,
        auto_eval_model: promptData.auto_eval_settings?.evaluation_model || 'llama-3.1-8b-instant',
        auto_eval_threshold: promptData.auto_eval_settings?.pass_threshold || 0.7
      });

    } catch (error) {
      console.error('Error loading prompt settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Clean the form data to prevent UUID issues
      const cleanedData = {
        ...formData,
        owner_id: formData.owner_id && formData.owner_id.trim() !== '' ? formData.owner_id : null,
        maintainers: formData.maintainers && formData.maintainers.length > 0 ? formData.maintainers : null
      };
      
      await updatePromptSettings(promptId, cleanedData);
      navigate(`/prompts/${promptId}`);
    } catch (error) {
      console.error('Error saving prompt settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deletePrompt(promptId, user.id);
      navigate(`/projects/${prompt.project_id}`);
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Failed to delete prompt');
    }
  };

  const searchMaintainers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const results = await searchUsersForMaintainers(query, user.organization_id);
      setSearchResults(results.filter(u => 
        u.id !== formData.owner_id && 
        !formData.maintainers.some(m => m.id === u.id)
      ));
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const addMaintainer = (user) => {
    setFormData(prev => ({
      ...prev,
      maintainers: [...prev.maintainers, user]
    }));
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeMaintainer = (userId) => {
    setFormData(prev => ({
      ...prev,
      maintainers: prev.maintainers.filter(m => m.id !== userId)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Prompt not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
              <Link to="/" className="hover:text-primary-600">Dashboard</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
                {prompt.project_name}
              </Link>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/prompts/${promptId}`} className="hover:text-primary-600">
                {prompt.name}
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-900">Settings</span>
            </nav>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Prompt Settings</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage your prompt configuration, auto-evaluation, and access settings
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => navigate(`/prompts/${promptId}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Basic Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt ID
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={promptId}
                      readOnly
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-600 cursor-not-allowed"
                    />
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(promptId);
                          setCopiedPromptId(true);
                          setTimeout(() => setCopiedPromptId(false), 2000);
                        } catch (err) {
                          // Fallback for older browsers
                          const tempInput = document.createElement('input');
                          tempInput.value = promptId;
                          document.body.appendChild(tempInput);
                          tempInput.select();
                          document.execCommand('copy');
                          document.body.removeChild(tempInput);
                          setCopiedPromptId(true);
                          setTimeout(() => setCopiedPromptId(false), 2000);
                        }
                      }}
                      className="flex items-center justify-center px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                      title="Copy Prompt ID"
                    >
                      {copiedPromptId ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe what this prompt does..."
                  />
                </div>
              </div>
            </div>

            {/* Access & Collaboration */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Access & Collaboration
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner
                  </label>
                  <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-600">
                        {prompt.owner_name ? prompt.owner_name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {prompt.owner_name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-500">Project Owner</div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maintainers
                  </label>
                  <div className="space-y-2">
                    {formData.maintainers.map((maintainer) => (
                      <div key={maintainer.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-celadon-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-celadon-700">
                              {maintainer.full_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{maintainer.full_name}</div>
                            <div className="text-sm text-gray-500">{maintainer.email}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeMaintainer(maintainer.id)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchMaintainers(e.target.value);
                        }}
                        placeholder="Search users to add as maintainers..."
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto">
                          {searchResults.map((user) => (
                            <button
                              key={user.id}
                              onClick={() => addMaintainer(user)}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2"
                            >
                              <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {user.full_name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium">{user.full_name}</div>
                                <div className="text-gray-500">{user.email}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SDK & Logging Settings */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Database className="h-5 w-5 mr-2" />
                  SDK & Logging
                </h3>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Enable SDK Logging</h4>
                    <p className="text-sm text-gray-500">
                      Allow this prompt to be logged when used via the SDK
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.enable_logging}
                      onChange={(e) => handleInputChange('enable_logging', e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Auto-evaluation Settings */}
            <div className="bg-white shadow-sm border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Globe className="h-5 w-5 mr-2" />
                  Auto-evaluation
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Enable Auto-evaluation</h4>
                    <p className="text-sm text-gray-500">
                      Automatically evaluate SDK logs using AI when logging is enabled
                    </p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.enable_logging}
                      onChange={(e) => handleInputChange('enable_logging', e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </div>
                </div>

                {formData.enable_logging && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-gray-800">
                            Auto-evaluation Enabled
                          </h3>
                          <div className="mt-2 text-sm text-gray-700">
                            <p>
                              When enabled, all SDK logs for this prompt will be automatically evaluated using AI. 
                              The system will assess the quality of responses based on relevance, accuracy, completeness, 
                              clarity, and helpfulness.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Danger Zone
                </h3>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Delete Prompt</h4>
                    <p className="text-sm text-gray-600">
                      Permanently delete this prompt and all its versions. This cannot be undone.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm font-medium"
                  >
                    Delete Prompt
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white shadow-sm border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(prompt.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="text-sm font-medium text-gray-900">
                    {new Date(prompt.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Logging</span>
                  <span className={`text-sm font-medium ${prompt.enable_logging ? 'text-celadon-600' : 'text-gray-500'}`}>
                    {prompt.enable_logging ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="bg-white shadow-sm border rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <Link 
                  to={`/prompts/${promptId}`}
                  className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Code
                </Link>
                <Link 
                  to={`/prompts/${promptId}/logs`}
                  className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Logs
                </Link>
                <Link 
                  to={`/prompts/${promptId}/pull-requests`}
                  className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <GitPullRequest className="h-4 w-4 mr-2" />
                  Pull Requests
                </Link>
                <Link 
                  to={`/prompts/${promptId}/collaborators`}
                  className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Collaborators
                </Link>
                <Link 
                  to={`/prompts/${promptId}/forks`}
                  className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors col-span-2"
                >
                  <GitFork className="h-4 w-4 mr-2" />
                  Forks
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md bg-white rounded-lg shadow-lg">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                <AlertTriangle className="h-6 w-6 text-gray-600" />
              </div>
              <div className="mt-3 text-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Delete Prompt
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete "{prompt.name}"? This action cannot be undone and will delete all versions, logs, and related data.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Delete Prompt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptSettings; 